import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/integrations/supabase/client';
import { brand } from '@/config/brand';
import {
  brandColorCssVariables,
  DEFAULT_BRAND_COLOR,
  normaliseHexColor,
} from '@/lib/brand-color';

export const BRANDING_SETTING_KEY = 'branding';

export interface Branding {
  /** Company name shown beside the logo and used as the badge fallback. */
  name: string;
  /** Two letters shown in the coloured square when no logo image is set. */
  initials: string;
  headerLogoUrl: string;
  footerLogoUrl: string;
  /** Text after the copyright symbol and auto-inserted year. */
  copyright: string;
  /** Notice shown at the bottom right of the page. */
  disclaimer: string;
  supportEmail: string;
  supportPhone: string;
  /** Hex accent colour used by buttons, links, focus rings and icons. */
  primaryColor: string;
}

/**
 * Built-in values. Used whenever a branding row is missing or a field is left
 * blank, so the site always renders something sensible.
 */
export const defaultBranding: Branding = {
  name: brand.name,
  initials: 'YB',
  headerLogoUrl: '',
  footerLogoUrl: '',
  copyright: `${brand.legalName}. All rights reserved.`,
  disclaimer: `${brand.name} is NOT insurance. It is a healthcare membership that provides direct access to care at affordable prices.`,
  supportEmail: 'support@your-branded-domain.com',
  supportPhone: '1-888-555-1212',
  primaryColor: DEFAULT_BRAND_COLOR,
};

const cleanString = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;

const cleanUrl = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const url = value.trim();
  if (url === '') return '';
  return /^(https?:)?\/\//i.test(url) || url.startsWith('data:image/') ? url : '';
};

/** Merges a stored settings value over the built-in defaults. */
export const normaliseBranding = (stored: unknown): Branding => {
  const value = (stored ?? {}) as Record<string, unknown>;
  return {
    name: cleanString(value.name, defaultBranding.name),
    initials: cleanString(value.initials, defaultBranding.initials).slice(0, 3).toUpperCase(),
    headerLogoUrl: cleanUrl(value.headerLogoUrl),
    footerLogoUrl: cleanUrl(value.footerLogoUrl),
    copyright: cleanString(value.copyright, defaultBranding.copyright),
    disclaimer: cleanString(value.disclaimer, defaultBranding.disclaimer),
    supportEmail: cleanString(value.supportEmail, defaultBranding.supportEmail),
    supportPhone: cleanString(value.supportPhone, defaultBranding.supportPhone),
    primaryColor: normaliseHexColor(value.primaryColor) || defaultBranding.primaryColor,
  };
};

/** Writes the brand colour onto the document so every token follows it. */
export const applyBrandColor = (hex: string) => {
  if (typeof document === 'undefined') return;
  const style = document.documentElement.style;
  Object.entries(brandColorCssVariables(hex)).forEach(([name, value]) => {
    style.setProperty(name, value);
  });
};

/** Dispatched after a save so open pages pick up the new branding immediately. */
export const BRANDING_UPDATED_EVENT = 'branding-updated';

export const fetchBranding = async (): Promise<Branding> => {
  const { data } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', BRANDING_SETTING_KEY)
    .maybeSingle();
  return normaliseBranding(data?.value);
};

const BrandingContext = createContext<Branding>(defaultBranding);

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const [branding, setBranding] = useState<Branding>(defaultBranding);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const next = await fetchBranding();
        if (active) {
          setBranding(next);
          applyBrandColor(next.primaryColor);
        }
      } catch (err) {
        console.error('Failed to load branding settings', err);
      }
    };

    load();
    window.addEventListener(BRANDING_UPDATED_EVENT, load);
    return () => {
      active = false;
      window.removeEventListener(BRANDING_UPDATED_EVENT, load);
    };
  }, []);

  const value = useMemo(() => branding, [branding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

/** Branding values for the current site. Falls back to the built-in defaults. */
export const useBranding = (): Branding => useContext(BrandingContext);

/** Tells every open page to reload the branding values. */
export const notifyBrandingUpdated = () => {
  window.dispatchEvent(new Event(BRANDING_UPDATED_EVENT));
};

/** Saves the branding row and notifies open pages. */
export const saveBranding = async (branding: Branding): Promise<void> => {
  const { error } = await supabase.from('app_settings').upsert(
    [
      {
        key: BRANDING_SETTING_KEY,
        value: { ...branding } as Record<string, string>,
        description: 'Company branding: logos, footer text and contact details',
        updated_at: new Date().toISOString(),
      },
    ],
    { onConflict: 'key' }
  );

  if (error) throw error;
  notifyBrandingUpdated();
};

